import { useCallback, useEffect, useRef, useState } from "react";
import { onMessage } from "firebase/messaging";
import { toast } from "sonner";
import { firebaseAuth, messaging } from "../config/firebase";
import NotificationService from "../services/Notifications/notifications.service";

// Pergunta de reserva: só para quem não recebe push (bloqueou notificações ou navegador sem suporte).
const FALLBACK_INTERVAL_MS = 90_000;

// Conferência de segurança para quem recebe push, caso o push falhe. Só roda
// com a aba visível e para de vez quando um push chega nesta página.
const SAFETY_CHECK_INTERVAL_MS = 10 * 60_000;

const getToken = () => firebaseAuth.currentUser?.getIdToken();

/** Evento da janela que abre a central de notificações (ouvido pelo NotificationCenter). */
export const OPEN_NOTIFICATIONS_EVENT = "notifications:open";

/** Botão de ação dos toasts de notificação (mesmo verde do "Ver todas" da central). */
export const toastActionButtonStyle = {
  background: "var(--color-g700)",
  color: "#fff",
  borderRadius: 7,
  fontWeight: 700,
};

// Toast com botão "Ver", que abre a central.
const notify = (message) =>
  toast(message, {
    action: {
      label: "Ver",
      onClick: () => window.dispatchEvent(new Event(OPEN_NOTIFICATIONS_EVENT)),
    },
    actionButtonStyle: toastActionButtonStyle,
  });

const hasPushPermission = () =>
  typeof Notification !== "undefined" && Notification.permission === "granted";

/**
 * Notificações do usuário.
 *
 * - Ao abrir: carrega a lista e avisa se houver não lidas.
 * - Aviso novo: o Firebase avisa a página na hora (onMessage), sem a página
 *   precisar perguntar. Ela só pergunta à API:
 *     - quando a aba volta a ficar visível (pega algo que tenha escapado);
 *     - a cada 90s, apenas se a pessoa não recebe push;
 *     - a cada 10 min, se recebe push mas nenhum push chegou ainda nesta
 *       página (conferência de segurança; para quando o primeiro push chega).
 * - "Novo" = id maior que o maior id da lista carregada do servidor ao abrir a
 *   página (guardado em maxIdRef, só na memória da aba). Todo aparelho parte
 *   do aviso mais recente do servidor, então avisos antigos não viram "novos".
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  // true até a lista inicial chegar do servidor (a central mostra "Carregando…").
  const [isLoading, setIsLoading] = useState(true);
  // Totais de cada filtro vindos da API (a lista carregada é só uma parte).
  const [counts, setCounts] = useState({
    visible: 0,
    unread: 0,
    liked: 0,
    hidden: 0,
  });
  const maxIdRef = useRef(null);
  const isCheckingRef = useRef(false);

  const refreshCounts = useCallback(async () => {
    try {
      setCounts(
        await NotificationService.getNotificationCounts(await getToken()),
      );
    } catch (error) {
      console.error("Erro ao contar notificações:", error);
    }
  }, []);

  /** Busca as próximas 20 do filtro, mais velhas que `beforeId`, e junta na lista. */
  const loadMore = useCallback(async (filter, beforeId) => {
    try {
      const response = await NotificationService.getUserNotifications(
        await getToken(),
        { filter, before: beforeId },
      );
      const items = Array.isArray(response.data.data) ? response.data.data : [];
      setNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        return [...prev, ...items.filter((n) => !existingIds.has(n.id))];
      });
    } catch (error) {
      console.error("Erro ao carregar mais notificações:", error);
    }
  }, []);

  const checkForNew = useCallback(async () => {
    // Ainda carregando a lista inicial, ou já existe uma verificação em andamento.
    if (maxIdRef.current === null || isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const latest = await NotificationService.getLatestNotifications(
        maxIdRef.current,
        await getToken(),
      );
      if (!Array.isArray(latest)) return;
      const fresh = latest.filter((n) => n.id > maxIdRef.current);
      if (!fresh.length) return;

      maxIdRef.current = Math.max(...fresh.map((n) => n.id));
      setNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        return [...fresh.filter((n) => !existingIds.has(n.id)), ...prev];
      });
      refreshCounts();

      if (fresh.length === 1) {
        notify(`Você tem uma nova notificação: ${fresh[0].title}`);
      } else {
        notify(`Você tem ${fresh.length} novas notificações`);
      }
    } catch (error) {
      console.error("Erro ao buscar novas notificações:", error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [refreshCounts]);

  const loadInitial = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await NotificationService.getUserNotifications(token);
      const items = Array.isArray(response.data.data) ? response.data.data : [];
      setNotifications(items);
      maxIdRef.current = items.reduce((max, n) => Math.max(max, n.id), 0);
      setIsLoading(false);
      refreshCounts();

      const { count, latest } =
        await NotificationService.getUnreadSummary(token);
      if (count === 1 && latest) {
        notify(`Você tem 1 notificação não lida: ${latest.title}`);
      } else if (count > 1) {
        notify(`Você tem ${count} notificações não lidas`);
      }
    } catch (error) {
      // Sem a lista não dá para saber o que é novo; tenta de novo na próxima rodada.
      console.error("Erro ao carregar notificações:", error);
    }
  }, [refreshCounts]);

  useEffect(() => {
    const tick = () =>
      maxIdRef.current === null ? loadInitial() : checkForNew();

    let unsubscribeFirebase = () => {};
    let isListeningToFirebase = false;
    // Vira true quando um push chega: prova que o push funciona neste aparelho.
    let isPushConfirmed = false;
    try {
      unsubscribeFirebase = onMessage(messaging, () => {
        isPushConfirmed = true;
        checkForNew();
      });
      isListeningToFirebase = true;
    } catch (error) {
      // Navegador sem suporte a push: segue só com a pergunta de reserva.
      console.warn("Firebase Messaging indisponível:", error);
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") tick();
    };

    // A permissão é conferida a cada vez, pois pode ser concedida com a página aberta.
    const receivesPush = () => isListeningToFirebase && hasPushPermission();

    // Quem recebe push não precisa perguntar periodicamente: o Firebase avisa.
    const fallbackTick = () => {
      if (maxIdRef.current !== null && receivesPush()) return;
      tick();
    };

    const safetyTick = () => {
      if (!receivesPush() || isPushConfirmed) return;
      if (document.visibilityState !== "visible") return;
      checkForNew();
    };

    loadInitial();
    const fallbackInterval = setInterval(fallbackTick, FALLBACK_INTERVAL_MS);
    const safetyInterval = setInterval(safetyTick, SAFETY_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      unsubscribeFirebase();
      clearInterval(fallbackInterval);
      clearInterval(safetyInterval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [loadInitial, checkForNew]);

  return {
    notifications,
    setNotifications,
    isLoading,
    counts,
    refreshCounts,
    loadMore,
  };
}
